import { Button, Stack } from '@mui/material'
import {
  curry,
  defaultTo,
  equals,
  filter,
  flatten,
  identity,
  ifElse,
  is,
  length,
  lensPath,
  over,
  pathSatisfies,
  pipe,
} from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Case, Switch, When } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import {
  VailAccordion,
  VailSelect,
  VailSwitch,
  VailTextField,
} from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg, useUrlParams } from '@/hooks'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType } from '@/utils/api'
import modifyPath from '@/utils/modifyPath'

import {
  localizedArray,
  SentimentCheckSettings,
  SupportedLocales,
} from '../../types/customComponents'
type SentimentCheckForm = {
  settings: SentimentCheckSettings
  name: string
  description: string
}

const defaultValues: SentimentCheckForm = {
  description: '',
  name: '',
  settings: {
    CollectOpenFeedback: false,
    NegButtonText: {
      en: "I didn't like it",
    },
    NegButtonValue: {
      en: 'Negative',
    },
    NegResponse: {
      en: ["I'm sorry to hear that."],
    },
    OpenFeedbackPrompt: {
      en: [
        'Please tell us why. Your clinical team will not see this feedback, so please do not enter medical questions or concerns intended for your care team.',
      ],
    },
    PosButtonText: {
      en: 'I liked it',
    },
    PosButtonValue: { en: 'Positive' },
    PosResponse: { en: ["I'm glad to hear that!"] },
    SentimentCheckCompletedNextStep: 'next step',
    SentimentPrompt: {
      en: ['What did you think about the digital assistant?'],
    },
  },
}

/*
 * This will check if all of the keys inside of the object are falsy,
 * @example
 * isValuesEmpty({en: ['']}) // true
 * isValuesEmpty({en: ['hello']}) // false
 * isValuesEmpty({en: ['hello', 'world'],es:['hola','mundo]}) // false
 * isValuesEmpty({en: ['hello', 'world'],es:['hola','mundo],fr:[]}) // false
 * @param {localizedArray} obj - The object to check
 * @returns {boolean} - Returns true if the object is empty, false otherwise
 */
const isValuesEmpty = pipe(
  defaultTo([]),
  Object.values,
  flatten,
  filter(Boolean),
  length,
  equals(0)
)

const setNullOnEmpty = (obj: localizedArray): localizedArray | null => {
  if (isValuesEmpty(obj)) return null
  return obj
}

const parseData = pipe<
  [SentimentCheckForm],
  SentimentCheckForm,
  SentimentCheckForm | null
>(
  over(lensPath(['settings', 'NegResponse']), setNullOnEmpty),
  over(lensPath(['settings', 'PosResponse']), setNullOnEmpty)
)

export const SettingsTab = () => {
  const form = useForm<SentimentCheckForm>()
  const navigate = useNavigate()
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()

  const createIntro = useCreateCustomComponent<SentimentCheckSettings>(orgID)
  const current = useGetCustomComponent<SentimentCheckSettings>(componentID)
  const updateIntro =
    useUpdateCustomComponent<SentimentCheckSettings>(componentID)
  const { reset } = form

  useEffect(() => {
    if (!componentID) return reset(defaultValues)
    if (!current.data) return
    const localizeString = curry(
      (pathToReplace: (string | number)[], str: string) =>
        //@ts-ignore
        ifElse(
          pathSatisfies(is(String), [str, 'en']),
          modifyPath([str, 'en'], [str, ...pathToReplace]),
          identity
        )
    )
    const localizeStringArray = localizeString(['en', 0])
    const updateSettings = pipe<
      [SentimentCheckSettings],
      SentimentCheckSettings,
      SentimentCheckSettings,
      SentimentCheckSettings,
      SentimentCheckSettings
    >(
      localizeStringArray('NegResponse'),
      localizeStringArray('OpenFeedbackPrompt'),
      localizeStringArray('PosResponse'),
      localizeStringArray('SentimentPrompt')
    )

    const { name, description, settings } = current.data
    reset({ description, name, settings: updateSettings(settings) })
  }, [componentID, current.data, reset])

  const onChangeSwitch =
    (registerField: keyof SentimentCheckSettings) => (open: boolean) => {
      if (open) form.register(`settings.${registerField}`)
      else form.unregister(`settings.${registerField}`)
    }

  const onSubmit = form.handleSubmit(async (data) => {
    const parsedData = parseData(data)

    if (!parsedData) return {}
    if (componentID)
      return await updateIntro.mutateAsync({
        ...parsedData,
        id: componentID,
        settings: parsedData.settings || {},
        type: ComponentType.sentimentCheck,
      })
    const res = await createIntro.mutateAsync({
      ...parsedData,
      settings: parsedData.settings || {},
      type: ComponentType.sentimentCheck,
    })
    return navigate(`../${res?.id}`)
  })

  const org = useOrg(orgID)
  const [{ lang = 'en' }] = useUrlParams()
  const languagesSupported = org.data?.languagesSupported || ['en']

  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        rowGap={2}
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save sentiment check')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              defaultValue={ComponentType.sentimentCheck}
              label="Type"
              name="type"
              options={[
                {
                  label: 'Sentiment Check',
                  value: ComponentType.sentimentCheck,
                },
              ]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          title="Configuration"
        >
          <LanguageSelector />

          <Switch>
            {languagesSupported.map((orgLang) => (
              <Case key={orgLang} condition={orgLang === lang}>
                <VailTextField
                  multiline
                  control={form.control}
                  label="Sentiment Prompt"
                  maxRows={5}
                  minRows={3}
                  name={`settings.SentimentPrompt.${
                    orgLang as SupportedLocales
                  }.0`}
                />
              </Case>
            ))}
          </Switch>
          <Stack flexDirection={{ md: 'row', xs: 'column' }} gap={2}>
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Positive response button"
                    name={`settings.PosButtonText.${
                      orgLang as SupportedLocales
                    }`}
                  />
                  <VailTextField
                    control={form.control}
                    label="Value"
                    name={`settings.PosButtonValue.${
                      orgLang as SupportedLocales
                    }`}
                  />
                  <VailTextField
                    control={form.control}
                    label="Positive Response"
                    name={`settings.PosResponse.${
                      orgLang as SupportedLocales
                    }.0`}
                    rules={{}}
                  />
                </Case>
              ))}
            </Switch>
          </Stack>
          <Stack flexDirection={{ md: 'row', xs: 'column' }} gap={2}>
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Negative response button"
                    name={`settings.NegButtonText.${
                      orgLang as SupportedLocales
                    }`}
                  />
                  <VailTextField
                    control={form.control}
                    label="Value"
                    name={`settings.NegButtonValue.${
                      orgLang as SupportedLocales
                    }`}
                  />
                  <VailTextField
                    control={form.control}
                    label="Negative Response"
                    name={`settings.NegResponse.${
                      orgLang as SupportedLocales
                    }.0`}
                    rules={{}}
                  />
                </Case>
              ))}
            </Switch>
          </Stack>
          <VailSwitch
            label="Collect open feedback"
            name="settings.CollectOpenFeedback"
            onChange={onChangeSwitch('OpenFeedbackPrompt')}
          />
          <When condition={!!form.watch('settings.CollectOpenFeedback')}>
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    multiline
                    control={form.control}
                    label="Open Feedback Prompt"
                    maxRows={5}
                    minRows={5}
                    name={`settings.OpenFeedbackPrompt.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          </When>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
